import {
  ManualAdjustmentKey,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  UnitRateKey,
} from '@aloft/toolkit-shared';

import { RefObject, useContext, useEffect, useMemo } from 'react';

import { ALERT_HOVER_PREVIEW_CELL_ATTR } from '../../../../hooks/useToaster/constants.ts';
import { getActiveAlertForPropertyGridCell } from '../../../../modules/alerts/getActiveAlertForPropertyGridCell.ts';
import {
  shouldPreserveAlertHoverPreview,
  syncAlertHoverPreview,
} from '../../../../modules/alerts/syncAlertHoverPreview.ts';
import { useAlerts } from '../../../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectHoveredPropertyGridCellKey } from '../../../../redux/features/selectors/referenceBarUISelectors.ts';
import {
  selectToastSuppressedAlertTypes,
  selectTriggeredAlerts,
} from '../../../../redux/features/selectors/uiSelectors.ts';
import { setFocusedPropertyGridCellKey } from '../../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { PropertyGridFocusContext } from '../../PropertyGridFocusContext.ts';
import { PropertyGridCellKey, PropertyGridFieldName } from '../../types.ts';
import { ALERT_TARGET_FIELD_TITLE } from '../styles.ts';

type PropertyGridCellKeyId =
  | PropertyGridFieldName
  | UnitRateKey
  | ManualAdjustmentKey
  | 'totalCostToCure';

type UsePropertyGridCellFocusParams = {
  cellKeyId: PropertyGridCellKeyId;
  propertyId: string;
  fieldRef: RefObject<HTMLElement>;
};

type UsePropertyGridCellFocusResult = {
  isAlertTarget: boolean;
  isAlertHighlighted: boolean;
  isAlertStyled: boolean;
  alertStyleVariant: 'error' | 'warning';
  alertCellTooltip?: string;
  isAlertToastVisible: boolean;
  clearAlertTarget: () => void;
};

export const usePropertyGridCellFocus = ({
  cellKeyId,
  propertyId,
  fieldRef,
}: UsePropertyGridCellFocusParams): UsePropertyGridCellFocusResult => {
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
  const cellKey: PropertyGridCellKey = isSubject
    ? `subject-${cellKeyId}`
    : `${propertyId}-${cellKeyId}`;

  const { activeTargetKey, registerField, unregisterField } = useContext(PropertyGridFocusContext);
  const hoveredPropertyGridCellKey = useAppSelector(selectHoveredPropertyGridCellKey);
  const triggeredAlerts = useAppSelector(selectTriggeredAlerts);
  const toastSuppressedAlertTypes = useAppSelector(selectToastSuppressedAlertTypes);
  const alerts = useAlerts();
  const isAlertTarget = activeTargetKey === cellKey;
  const isAlertHoverTarget = !isAlertTarget && hoveredPropertyGridCellKey === cellKey;
  const isAlertHighlighted = isAlertTarget || isAlertHoverTarget;
  const targetingAlert = useMemo(
    () => getActiveAlertForPropertyGridCell(alerts, cellKey),
    [alerts, cellKey],
  );
  const isTriggeredAlertCell = Boolean(
    targetingAlert && triggeredAlerts.includes(targetingAlert.type),
  );
  const isAlertStyled = isAlertHighlighted || isTriggeredAlertCell;
  const alertStyleVariant = targetingAlert?.severity ?? 'error';
  const isAlertToastVisible = Boolean(
    targetingAlert &&
      triggeredAlerts.includes(targetingAlert.type) &&
      !toastSuppressedAlertTypes.includes(targetingAlert.type),
  );
  const alertCellTooltip =
    targetingAlert?.fixingInfo.targetCellTooltip ??
    (isAlertStyled ? ALERT_TARGET_FIELD_TITLE : undefined);
  const dispatch = useAppDispatch();

  useEffect(() => {
    registerField(cellKey, fieldRef);
    return () => unregisterField(cellKey);
  }, [cellKey]);

  useEffect(() => {
    if (!targetingAlert) {
      return undefined;
    }

    let element: HTMLElement | null = null;
    let removeListeners: (() => void) | undefined;

    const setup = () => {
      element = fieldRef.current;
      if (!element) {
        return;
      }

      element.setAttribute(ALERT_HOVER_PREVIEW_CELL_ATTR, '');

      const handleMouseEnter = () => {
        syncAlertHoverPreview(dispatch, targetingAlert);
      };

      const handleMouseLeave = (event: MouseEvent) => {
        if (element?.contains(event.relatedTarget as Node)) {
          return;
        }
        if (shouldPreserveAlertHoverPreview(event.relatedTarget as HTMLElement | null)) {
          return;
        }
        syncAlertHoverPreview(dispatch, null);
      };

      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('mouseleave', handleMouseLeave);

      removeListeners = () => {
        element?.removeAttribute(ALERT_HOVER_PREVIEW_CELL_ATTR);
        element?.removeEventListener('mouseenter', handleMouseEnter);
        element?.removeEventListener('mouseleave', handleMouseLeave);
      };
    };

    const raf = requestAnimationFrame(setup);

    return () => {
      cancelAnimationFrame(raf);
      removeListeners?.();
    };
  }, [cellKey, dispatch, targetingAlert]);

  const clearAlertTarget = () => {
    if (isAlertTarget) dispatch(setFocusedPropertyGridCellKey(undefined));
  };

  return {
    isAlertTarget,
    isAlertHighlighted,
    isAlertStyled,
    alertStyleVariant,
    alertCellTooltip,
    isAlertToastVisible,
    clearAlertTarget,
  };
};
